import React, { useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Chip,
  Stack,
  Button,
} from "@mui/material";

const RoomDayWiseCard = React.memo(
  ({
    room,
    dayWiseDetails,
    onDetailChange,
    onApplySameRate,
    onClearRates,
    showValidationErrors,
    colors,
  }) => {
    const handleFieldChange = useCallback(
      (dateTimestamp, field, value) => {
        const numValue = value === "" ? "" : parseFloat(value) || 0;
        onDetailChange(room._id, dateTimestamp, field, numValue);
      },
      [room._id, onDetailChange]
    );

    const roomName = room.room_id?.room_no || "Unknown";
    const roomType = room.room_id?.roomtype_id?.name || "Room";
    const mealPlanName = room.meal_plan?.name || "—";

    const roomSubtotal = useMemo(() => {
      return dayWiseDetails.reduce((sum, detail) => {
        const amount = detail.amount === "" ? 0 : Number(detail.amount);
        const extraBed =
          detail.extra_bed_price === "" ? 0 : Number(detail.extra_bed_price);
        const mealPlan =
          detail.meal_plan_price === "" ? 0 : Number(detail.meal_plan_price);
        return sum + amount + extraBed + mealPlan;
      }, 0);
    }, [dayWiseDetails]);

    const hasExistingData = room.day_wise_details?.length > 0;

    return (
      <Box
        sx={{
          p: 2.5,
          borderRadius: 1,
          bgcolor: colors.primary[400],
          border: `1px solid ${colors.grey?.[700] || "#424242"}`,
        }}
      >
        {/* Room Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ color: colors.greenAccent?.[400] || "#4caf50" }}
              >
                Room no: {roomName} ({roomType})
              </Typography>
              {hasExistingData && (
                <Chip
                  label="Has Rates"
                  size="small"
                  sx={{
                    fontSize: "0.65rem",
                    height: 20,
                    bgcolor: colors.blueAccent?.[700] || "#1976d2",
                    color: "#fff",
                  }}
                />
              )}
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              mt={1}
              flexWrap="wrap"
              useFlexGap
            >
              {dayWiseDetails.map((detail) => (
                <Chip
                  key={detail.date}
                  label={detail.dateLabel}
                  size="small"
                  sx={{
                    fontSize: "0.75rem",
                    bgcolor: colors.greenAccent?.[700] || "#388e3c",
                    color: "#fff",
                  }}
                />
              ))}
            </Stack>
          </Box>
          <Box textAlign="right">
            <Typography
              variant="caption"
              color={colors.grey?.[300] || "text.secondary"}
            >
              Room Subtotal
            </Typography>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: colors.greenAccent?.[400] || "#4caf50" }}
            >
              ₹{roomSubtotal.toLocaleString("en-IN")}
            </Typography>
          </Box>
        </Stack>

        {/* Guest Info Row */}
        <Grid container spacing={2} mb={2}>
          <Grid item xs={6} sm={3}>
            <Typography
              variant="caption"
              color={colors.grey?.[300] || "text.secondary"}
            >
              No. of adults
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={room.no_of_adults || 0}
              disabled
              sx={{ mt: 0.5 }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography
              variant="caption"
              color={colors.grey?.[300] || "text.secondary"}
            >
              No. of children
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={room.no_of_childs || 0}
              disabled
              sx={{ mt: 0.5 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="caption"
              color={colors.grey?.[300] || "text.secondary"}
            >
              Meal plan
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                p: 1,
                borderRadius: 1,
                bgcolor: colors.primary[500],
                border: `1px solid ${colors.grey?.[700] || "#424242"}`,
              }}
            >
              <Typography variant="body2">
                <strong>{mealPlanName}</strong>
                {room.meal_plan?.rate > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      ml: 1,
                      color: colors.greenAccent?.[400] || "#4caf50",
                    }}
                  >
                    ₹{room.meal_plan.rate}
                  </Typography>
                )}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Day-wise Rate Grid */}
        <Box sx={{ overflowX: "auto", pb: 1 }}>
          <Grid container spacing={1.5}>
            {dayWiseDetails.map((detail) => {
              const hasAmountError =
                showValidationErrors && detail.amount === "";

              return (
                <Grid item xs={6} sm={4} md={3} lg={2} key={detail.date}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: colors.primary[500],
                      border: `1px solid ${
                        hasAmountError
                          ? colors.redAccent?.[500] || "#f44336"
                          : colors.grey?.[700] || "#424242"
                      }`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      display="block"
                      mb={1}
                      color={colors.grey?.[300] || "text.secondary"}
                    >
                      {detail.shortLabel}
                    </Typography>

                    <Stack spacing={1}>
                      <Box>
                        <Typography
                          variant="caption"
                          fontSize="0.65rem"
                          sx={{
                            color: hasAmountError
                              ? colors.redAccent?.[400] || "#ef5350"
                              : colors.grey?.[300] || "text.secondary",
                          }}
                        >
                          Room Rate *
                        </Typography>
                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          placeholder="Required"
                          value={detail.amount}
                          error={hasAmountError}
                          onChange={(e) =>
                            handleFieldChange(
                              detail.date,
                              "amount",
                              e.target.value
                            )
                          }
                          InputProps={{
                            sx: { fontSize: "0.85rem" },
                          }}
                        />
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          fontSize="0.65rem"
                          color={colors.grey?.[300] || "text.secondary"}
                        >
                          Extra Bed
                        </Typography>
                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          placeholder="—"
                          value={detail.extra_bed_price}
                          onChange={(e) =>
                            handleFieldChange(
                              detail.date,
                              "extra_bed_price",
                              e.target.value
                            )
                          }
                          InputProps={{
                            sx: { fontSize: "0.85rem" },
                          }}
                        />
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          fontSize="0.65rem"
                          color={colors.grey?.[300] || "text.secondary"}
                        >
                          Meal Plan
                        </Typography>
                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          placeholder="—"
                          value={detail.meal_plan_price}
                          onChange={(e) =>
                            handleFieldChange(
                              detail.date,
                              "meal_plan_price",
                              e.target.value
                            )
                          }
                          InputProps={{
                            sx: { fontSize: "0.85rem" },
                          }}
                        />
                      </Box>
                    </Stack>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Quick Actions */}
        <Stack
          direction="row"
          spacing={1}
          mt={2}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
        >
          <Typography
            variant="caption"
            color={colors.grey?.[300] || "text.secondary"}
          >
            Quick:
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onApplySameRate(room._id)}
            sx={{ fontSize: "0.7rem", textTransform: "none" }}
          >
            Apply same rate to all days
          </Button>
          <Button
            size="small"
            color="error"
            onClick={() => onClearRates(room._id)}
            sx={{ fontSize: "0.7rem", textTransform: "none" }}
          >
            Clear all rates
          </Button>
        </Stack>
      </Box>
    );
  }
);

RoomDayWiseCard.displayName = "RoomDayWiseCard";

export default RoomDayWiseCard;